import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useProducts } from "@/context/ProductsContext";
import { imgSrc } from "@/lib/api";
import { MaskLines, FadeUp, Eyebrow } from "@/components/site/Reveal";

const Row = ({ p, i, onEnter }) => (
  <Link to={`/products/${p.slug}`} data-testid={`index-row-${p.slug}`} onMouseEnter={() => onEnter(p)}
    className="group relative grid grid-cols-[auto_1fr_auto] items-center gap-4 border-b border-black/15 py-5 md:grid-cols-[80px_1fr_220px_140px_40px] md:py-7">
    <span className="absolute inset-0 origin-bottom scale-y-0 bg-[#FFC400] transition-transform duration-500 ease-out group-hover:scale-y-100" />
    <span className="relative hidden font-mono text-xs text-zinc-600 md:block">{String(i + 1).padStart(2, "0")}</span>
    <span className="spec-frame relative h-14 w-14 shrink-0 overflow-hidden md:hidden"><img src={imgSrc(p.image)} alt="" className="h-full w-full object-cover" /></span>
    <span className="relative flex items-center gap-3">
      <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: p.swatch }} />
      <span className="font-wide text-lg font-bold uppercase tracking-tight transition-transform duration-500 group-hover:translate-x-2 md:text-3xl">{p.short}</span>
      {p.isNew && <span className="bg-[#FF5A00] px-1.5 py-0.5 font-mono text-[10px] uppercase text-white">New</span>}
    </span>
    <span className="relative hidden font-mono text-xs uppercase tracking-[0.15em] text-zinc-700 md:block">{p.category}</span>
    <span className="relative hidden font-mono text-xs text-zinc-700 md:block">MOQ 100 kg</span>
    <ArrowRight className="relative h-5 w-5 transition-transform duration-500 group-hover:-rotate-45" />
  </Link>
);

export const IndexList = () => {
  const { products: PRODUCTS } = useProducts();
  const [active, setActive] = useState(null);
  const x = useSpring(useMotionValue(0), { stiffness: 200, damping: 25 });
  const y = useSpring(useMotionValue(0), { stiffness: 200, damping: 25 });
  const onMove = (e) => { x.set(e.clientX + 24); y.set(e.clientY - 120); };

  return (
    <section data-testid="index-section" className="relative mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
      <div className="grid gap-8 md:grid-cols-12">
        <div className="md:col-span-8">
          <Eyebrow>The index — {PRODUCTS.length} ink systems</Eyebrow>
          <h2 className="font-wide mt-6 text-4xl font-black uppercase leading-[0.9] tracking-tight sm:text-5xl lg:text-7xl">
            <MaskLines onView lines={["One factory.", "Every substrate."]} />
          </h2>
        </div>
        <FadeUp className="self-end md:col-span-4">
          <Link to="/products" data-testid="index-view-all" className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em]">
            <span className="border-b-2 border-[#FF5A00] pb-1">View full catalogue</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
      <FadeUp className="mt-14 border-t border-black" onMouseMove={onMove} onMouseLeave={() => setActive(null)}>
        {PRODUCTS.map((p, i) => <Row key={p.slug} p={p} i={i} onEnter={setActive} />)}
      </FadeUp>
      <motion.div style={{ x, y }} className="pointer-events-none fixed left-0 top-0 z-40 hidden md:block">
        <AnimatePresence>
          {active && (
            <motion.div key={active.slug} initial={{ opacity: 0, scale: 0.85, rotate: -4 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.35 }} className="spec-frame absolute h-[240px] w-[200px] overflow-hidden border-4 border-white shadow-2xl">
              <img src={imgSrc(active.image)} alt="" className="h-full w-full object-cover" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};
